import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import { T, FONTS, SHADOW } from '../constants/theme.js';
import { getCurrentUser, fetchNotifications, getToken, getTenantSlug } from '../services/api.js';
import { useOnlineStatus } from '../hooks/useOnlineStatus.js';
import { useSyncStore } from '../stores/syncStore.js';
import { useSubscription } from '../hooks/useSubscription.js';
import SubscriptionWatchdog, { WatchBadge } from '../components/SubscriptionWatchdog.jsx';

// syncEngine and genericSyncEngine are removed in the desktop build.
// All mutation queuing goes through the SyncWorker IPC (window.pms).
// These are permanent no-ops — the !window.pms branches in the component
// body are kept for documentation but will never execute.
async function _pendingQueueCount()   { return 0; }
async function _genericPendingCount() { return 0; }
async function _flushQueue()          {}
async function _flushGenericQueue()   {}

const NAV = [
  { id:'dashboard',    icon:'📊', label:'Dashboard'            },
  { id:'frontdesk',   icon:'🛎️', label:'Front Desk',   roles:['ADMIN','TENANT_ADMIN','MANAGER','FRONT_DESK'] },
  { id:'reservations', icon:'📋', label:'Reservations', roles:['ADMIN','TENANT_ADMIN','MANAGER','FRONT_DESK','RESERVATIONIST','READONLY'] },
  { id:'rooms',        icon:'🏨', label:'Room Status',  roles:['ADMIN','TENANT_ADMIN','MANAGER','FRONT_DESK','HOUSEKEEPER'] },
  { id:'housekeeping', icon:'🧹', label:'Housekeeping', roles:['ADMIN','TENANT_ADMIN','MANAGER','FRONT_DESK','HOUSEKEEPER'] },
  { id:'folio',        icon:'🧾', label:'Smart Folio',  roles:['ADMIN','TENANT_ADMIN','MANAGER','FRONT_DESK','ACCOUNTANT'] },
  { id:'guests',       icon:'👥', label:'Guests',       roles:['ADMIN','TENANT_ADMIN','MANAGER','FRONT_DESK','RESERVATIONIST','ACCOUNTANT'] },
  { id:'corporate-accounts', icon:'🏢', label:'Corporate Accounts', roles:['ADMIN','TENANT_ADMIN','MANAGER','ACCOUNTANT','FRONT_DESK','RESERVATIONIST'] },
  { id:'booking',      icon:'🌐', label:'Online Booking', roles:['ADMIN','TENANT_ADMIN','MANAGER'], desktopHide:true },
  { id:'staff',        icon:'👤', label:'Staff & Users', roles:['ADMIN','TENANT_ADMIN'] },
  { id:'reports',      icon:'📈', label:'Reports',        roles:['ADMIN','TENANT_ADMIN','MANAGER','ACCOUNTANT'] },
  { id:'district-report', icon:'🏛️', label:'District Report', roles:['ADMIN','TENANT_ADMIN','MANAGER','FRONT_DESK'] },
  { id:'financial',    icon:'💹', label:'Financials',     roles:['ADMIN','TENANT_ADMIN','MANAGER','ACCOUNTANT'] },
  { id:'settings',     icon:'⚙️',  label:'Property Setup', group:'settings', roles:['ADMIN','TENANT_ADMIN','MANAGER'] },
  { id:'email-config', icon:'✉️',  label:'Email Config',   group:'settings', roles:['ADMIN','TENANT_ADMIN'], desktopHide:true },
  { id:'audit-logs',   icon:'📋',  label:'Audit Logs',     group:'settings', roles:['ADMIN','TENANT_ADMIN'], desktopHide:true },
];

// Roles that have full unrestricted access
const FULL_ACCESS_ROLES = ['ADMIN', 'TENANT_ADMIN'];

function canAccess(item, role) {
  if (!role) return true;                          // no role claim → show all (dev mode)
  if (FULL_ACCESS_ROLES.includes(role)) return true;
  if (!item.roles) return true;                    // no restriction on this item
  return item.roles.includes(role);
}

const MODULE_ICONS = { reservation:'🏨', restaurant:'🍽️', pos:'🛒', spa:'💆', events:'🎪', accounting:'📒' };

const NOTIF_META = {
  arrival:     { icon:'🛎️', label:'Arrivals'         },
  departure:   { icon:'🚪', label:'Departures'        },
  housekeeping:{ icon:'🧹', label:'Housekeeping'      },
  booking:     { icon:'📋', label:'Online booking'    },
  folio:       { icon:'💰', label:'Outstanding folio' },
  default:     { icon:'🔔', label:'Notification'      },
};

/**
 * SidebarClock — isolated in its own memo component so the 30-second tick
 * ONLY re-renders this tiny element, not the entire FrontDeskLayout tree.
 * This was the root cause of sidebar nav requiring multiple clicks:
 * the layout re-rendered every 30s, re-creating NavBtn instances mid-click.
 */
const SidebarClock = memo(function SidebarClock() {
  const [clock, setClock] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 30000);
    return () => clearInterval(t);
  }, []);
  const today = clock.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  const time  = clock.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return (
    <div className="topbar-clock" style={{ textAlign: 'right', lineHeight: 1.2, flexShrink: 0 }}>
      <p style={{ fontSize: 11, color: '#4A6080', fontWeight: 600 }}>{today}</p>
      <p style={{ fontSize: 10, color: '#3A4F66', fontFamily: 'monospace' }}>{time}</p>
    </div>
  );
});

/**
 * IsolatedWatchBadge / IsolatedSubscriptionWatchdog
 *
 * These two memo-wrapped wrappers own `useSubscription()` internally so the
 * 1-second countdown tick ONLY re-renders these tiny elements — never the
 * surrounding FrontDeskLayout with all its sidebar NavBtns.
 *
 * Before this fix: `useSubscription()` was called directly in FrontDeskLayout
 * and its `setTick` interval fired every second, causing the entire layout
 * (including every sidebar button) to re-render every second.  Clicks that
 * arrived during a re-render landed on a DOM node that was being replaced,
 * registering nothing.  Staff had to click multiple times.
 */
const IsolatedWatchBadge = memo(function IsolatedWatchBadge() {
  const subscription = useSubscription();
  return <WatchBadge subscription={subscription} />;
});

const IsolatedSubscriptionWatchdog = memo(function IsolatedSubscriptionWatchdog({ onLogout, tenantSlug }) {
  const subscription = useSubscription();
  return <SubscriptionWatchdog subscription={subscription} onLogout={onLogout} tenantSlug={tenantSlug} />;
});

export default function FrontDeskLayout({ children, currentView, property, tenantSlug, modules, onNavigate, onLogout, offlineBoot = false }) {
  const [collapsed,    setCollapsed]   = useState(false);
  const [mobileOpen,   setMobileOpen]  = useState(false);
  const [showMods,     setShowMods]    = useState(false);
  const [showNotif,    setShowNotif]   = useState(false);
  const [showProfile,  setShowProfile] = useState(false);
  const [settingsOpen, setSettingsOpen]= useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifRead,    setNotifRead]   = useState(false);
  const [pendingSync,  setPendingSync] = useState(0);   // combined: old + generic queue
  const [syncState,    setSyncState]   = useState(null); // null | { done, total, label, source }
  const [syncLocked,   setSyncLocked]  = useState(false);
  const [syncHalted,   setSyncHalted]  = useState(null); // null | { reason, remaining }
  const [conflictCount,setConflictCount] = useState(0);
  const { online: isOnline } = useOnlineStatus();

  // ── Subscription watchdog — polls Main Hall tunnel every 5 min ────────────
  // ── Desktop: targeted Zustand selectors — only re-render when the specific
  //    field changes, not on every store update (replaces full-store subscription
  //    which re-rendered the entire layout on any sync event)
  const desktopOnline    = useSyncStore(s => window.pms ? s.online             : null);
  const desktopPending   = useSyncStore(s => window.pms ? s.pendingQueueCount  : null);
  const desktopPhase     = useSyncStore(s => window.pms ? s.phase              : null);
  const desktopConflicts = useSyncStore(s => window.pms ? (s.conflictCount ?? 0) : 0);

  // Desktop: conflict count from SQLite conflict_log via IPC
  useEffect(() => {
    if (!window.pms?.queryConflicts) return;
    const refresh = async () => {
      try {
        const r = await window.pms.queryConflicts();
        if (r.success) setConflictCount((r.data ?? []).length);
      } catch (_) {}
    };
    refresh();
    const onConflicts = () => refresh();
    window.addEventListener('hotelmis:desktop:conflicts', onConflicts);
    return () => window.removeEventListener('hotelmis:desktop:conflicts', onConflicts);
  }, []);

  const jwtUser    = getCurrentUser();
  const userFirst  = jwtUser?.firstName || jwtUser?.given_name || '';
  const userLast   = jwtUser?.lastName  || jwtUser?.family_name || '';
  const userRole   = jwtUser?.role || jwtUser?.roles?.[0] || '';
  const displayName = (userFirst && userLast ? `${userFirst} ${userLast}` : userFirst || jwtUser?.sub || 'Staff').trim();
  const initials   = displayName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || 'S';

  const modsRef    = useRef(null);
  const notifRef   = useRef(null);
  const profileRef = useRef(null);

  useEffect(() => {
  }, []);

  const loadNotifications = useCallback(async () => {
    if (!property?.id) return;
    try {
      const data = await fetchNotifications(property.id);
      setNotifications(Array.isArray(data) ? data : []);
      setNotifRead(false);
    } catch (_) { /* silent — notifications are non-critical */ }
  }, [property?.id]);

  useEffect(() => {
    loadNotifications();
    const t = setInterval(loadNotifications, 3 * 60 * 1000); // refresh every 3 min
    return () => clearInterval(t);
  }, [loadNotifications]);

  const refreshPending = useCallback(async () => {
    try {
      if (window.pms) {
        // Desktop path — prefer the Zustand store (already up-to-date from push)
        // but fall back to a direct IPC poll for the initial seed.
        const storeState = useSyncStore.getState();
        if (storeState.connectionState !== 'UNKNOWN') {
          setPendingSync(storeState.pendingQueueCount);
          if (storeState.conflictCount > 0) setConflictCount(storeState.conflictCount);
        } else {
          // First call before any push has arrived — poll directly
          const r      = await window.pms.getSyncStatus();
          const status = r?.data ?? r ?? {};
          setPendingSync(Number(status.pending ?? 0));
          const ipcConflicts = Number(status.conflicts ?? 0);
          if (ipcConflicts > 0) setConflictCount(ipcConflicts);
        }
      } else {
        // Browser path — unchanged
        const [old, gen] = await Promise.all([
          _pendingQueueCount().catch(() => 0),
          _genericPendingCount().catch(() => 0),
        ]);
        setPendingSync(old + gen);
      }
    } catch (_) {}
  }, []);

  // ── Reactive: Zustand selectors above (desktopPending / desktopConflicts)
  //    already update reactively, so we only need to sync them to local state
  //    for the browser path (where they come from the refreshPending callback).
  useEffect(() => {
    if (!window.pms) return;
    if (desktopPending  != null) setPendingSync(desktopPending);
    if (desktopConflicts > 0)   setConflictCount(desktopConflicts);
  }, [desktopPending, desktopConflicts]);

  // Track sync progress + conflict state for BOTH the legacy and generic engines.
  useEffect(() => {
    refreshPending();

    // ── Legacy engine ────────────────────────────────────────────────────
    const onMutated       = () => refreshPending();
    const onLegacyStart   = e  => { setSyncHalted(null); setSyncState({ done: 0, total: e.detail?.total ?? 1, label: 'Syncing…', source: 'legacy' }); setSyncLocked(true); refreshPending(); };
    const onLegacyProg    = e  => { setSyncState(s => ({ ...s, ...e.detail, source: 'legacy' })); };
    const onLegacyDone    = () => { setSyncState(null); setSyncLocked(false); refreshPending(); };

    // ── Generic engine ───────────────────────────────────────────────────
    const onGenericLock   = () => { setSyncHalted(null); setSyncLocked(true); };
    const onGenericUnlock = () => { setSyncLocked(false); };
    const onGenericStart  = e  => {
      setSyncHalted(null);
      setSyncState({ done: 0, total: e.detail?.total ?? 1, label: 'Syncing offline changes…', source: 'generic' });
      refreshPending();
    };
    const onGenericProg   = e  => {
      setSyncState(s => ({
        ...s,
        done:  e.detail?.done  ?? s?.done  ?? 0,
        total: e.detail?.total ?? s?.total ?? 1,
        label: e.detail?.entityType
          ? `Syncing ${e.detail.entityType.toLowerCase().replace('_', ' ')}…`
          : 'Syncing…',
        source: 'generic',
      }));
    };
    const onGenericDone   = e  => {
      setSyncState(null);
      setSyncLocked(false);
      if (e.detail?.failed > 0) {
        setConflictCount(c => c + e.detail.failed);
      }
      refreshPending();
    };
    const onGenericHalted = e  => {
      setSyncState(null);
      setSyncLocked(false);
      setSyncHalted({ reason: e.detail?.reason, remaining: e.detail?.remainingItems ?? 0 });
      refreshPending();
    };

    const onDesktopConflicts = e => {
      const n = Number(e.detail?.count ?? 0);
      if (n > 0) setConflictCount(n);
    };

    window.addEventListener('hotelmis:data:mutated',          onMutated);

    window.addEventListener('hotelmis:sync:start',            onLegacyStart);
    window.addEventListener('hotelmis:sync:progress',         onLegacyProg);
    window.addEventListener('hotelmis:sync:done',             onLegacyDone);

    window.addEventListener('hotelmis:generic:sync:lock',     onGenericLock);
    window.addEventListener('hotelmis:generic:sync:unlock',   onGenericUnlock);
    window.addEventListener('hotelmis:generic:sync:start',    onGenericStart);
    window.addEventListener('hotelmis:generic:sync:progress', onGenericProg);
    window.addEventListener('hotelmis:generic:sync:done',     onGenericDone);
    window.addEventListener('hotelmis:generic:sync:halted',   onGenericHalted);
    window.addEventListener('hotelmis:desktop:conflicts',     onDesktopConflicts);

    return () => {
      window.removeEventListener('hotelmis:data:mutated',          onMutated);

      window.removeEventListener('hotelmis:sync:start',            onLegacyStart);
      window.removeEventListener('hotelmis:sync:progress',         onLegacyProg);
      window.removeEventListener('hotelmis:sync:done',             onLegacyDone);

      window.removeEventListener('hotelmis:generic:sync:lock',     onGenericLock);
      window.removeEventListener('hotelmis:generic:sync:unlock',   onGenericUnlock);
      window.removeEventListener('hotelmis:generic:sync:start',    onGenericStart);
      window.removeEventListener('hotelmis:generic:sync:progress', onGenericProg);
      window.removeEventListener('hotelmis:generic:sync:done',     onGenericDone);
      window.removeEventListener('hotelmis:generic:sync:halted',   onGenericHalted);
      window.removeEventListener('hotelmis:desktop:conflicts',     onDesktopConflicts);
    };
  }, [refreshPending]);

  // When we come back online, flush both queues and reload notifications
  useEffect(() => {
    if (isOnline) {
      setSyncHalted(null);
      loadNotifications();
      // Browser-only: flush the IndexedDB offline mutation queues.
      // Desktop (Electron) routes mutations through the SyncWorker IPC —
      // calling these functions would dispatch hotelmis:sync:done with an
      // empty queue and cause every useOfflineInvalidation hook to reload.
      if (!window.pms) {
        _flushQueue(getToken, getTenantSlug);
        _flushGenericQueue(getToken, getTenantSlug);
      }
    }
  }, [isOnline, loadNotifications]);

  useEffect(() => {
    const h = e => {
      if (modsRef.current    && !modsRef.current.contains(e.target))    setShowMods(false);
      if (notifRef.current   && !notifRef.current.contains(e.target))   setShowNotif(false);
      if (profileRef.current && !profileRef.current.contains(e.target)) setShowProfile(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    if (['settings','email-config'].includes(currentView)) setSettingsOpen(true);
  }, [currentView]);

  // Close mobile sidebar on resize to desktop
  useEffect(() => {
    const handler = () => { if (window.innerWidth > 768) setMobileOpen(false); };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const W = collapsed ? 58 : 224;
  const isActive = id => currentView === id
    || (id === 'reservations' && ['new-reservation','reservation-detail'].includes(currentView))
    || (id === 'staff' && ['staff-detail','staff-logs'].includes(currentView));

  const handleNavigate = (id) => {
    onNavigate(id);
    setMobileOpen(false);
  };

  const otherModules = (modules || []).filter(m => m.moduleId !== 'reservation');

  const currentLabel = NAV.find(n => isActive(n.id))?.label
    || (currentView === 'new-reservation'    ? '+ New Reservation'
    :   currentView === 'reservation-detail' ? 'Reservation Detail'
    :   currentView === 'email-config'       ? 'Email Configuration'
    :   currentView === 'financial'          ? 'Financial Statements'
    :   currentView === 'district-report'    ? 'District Guest Report'
    :   currentView === 'staff-detail'       ? 'Staff Profile'
    :   currentView === 'staff-logs'         ? 'Staff Activity Logs'
    :   currentView === 'audit-logs'         ? 'Audit Logs'
    :   'Front Desk');

  const NavBtn = ({ item }) => {
    const active = isActive(item.id);
    return (
      <button onClick={() => handleNavigate(item.id)} title={collapsed ? item.label : ''}
        style={{
          width:'100%', display:'flex', alignItems:'center',
          gap: collapsed ? 0 : 9,
          background: active ? `${T.teal}28` : 'none',
          border:`1px solid ${active ? `${T.teal}50` : 'transparent'}`,
          borderRadius:9, padding: collapsed ? '10px 0' : '8px 10px',
          cursor:'pointer', fontFamily:FONTS.body, textAlign:'left',
          marginBottom:1, justifyContent: collapsed ? 'center' : 'flex-start',
          transition:'all .15s',
        }}
        onMouseEnter={e => { if(!active) e.currentTarget.style.background = `${T.teal}14`; }}
        onMouseLeave={e => { if(!active) e.currentTarget.style.background = active?`${T.teal}28`:'none'; }}>
        <span style={{ fontSize:16, flexShrink:0, lineHeight:1 }}>{item.icon}</span>
        {!collapsed && (
          <span style={{ fontSize:13, fontWeight:active?700:500, color:active?T.teal:'#8A9BB5', whiteSpace:'nowrap', letterSpacing:.1 }}>
            {item.label}
          </span>
        )}
      </button>
    );
  };

  const isDesktop   = !!window.pms;
  const mainNav     = NAV.filter(n => !n.group && canAccess(n, userRole) && !(isDesktop && n.desktopHide));
  const settingsNav = NAV.filter(n => n.group === 'settings' && canAccess(n, userRole) && !(isDesktop && n.desktopHide));

  return (
    <div style={{ display:'flex', minHeight:'100vh', background:T.bg, fontFamily:FONTS.body }}>

      {/* ── Mobile overlay ── */}
      <div
        className={`sidebar-overlay${mobileOpen ? '' : ' hidden'}`}
        onClick={() => setMobileOpen(false)}
      />

      {/* ── Sidebar ── */}
      <aside className={`fd-sidebar${mobileOpen ? ' mobile-open' : ''}`} style={{
        width:W, flexShrink:0, background:T.navyDeep,
        borderRight:`1px solid ${T.navyLight}`,
        display:'flex', flexDirection:'column',
        position:'fixed', inset:'0 auto 0 0', zIndex:200,
        transition:'width .22s ease',
        overflow:'hidden',
      }}>
        {/* Brand */}
        <div style={{ padding:'14px 12px', borderBottom:`1px solid ${T.navyLight}`,
          display:'flex', alignItems:'center', gap:8, whiteSpace:'nowrap', minHeight:58 }}>
          <div style={{ width:32, height:32, borderRadius:8, background:T.teal, flexShrink:0,
            display:'flex', alignItems:'center', justifyContent:'center', fontSize:16,
            cursor:'pointer' }} onClick={() => setCollapsed(c => !c)} title="Toggle sidebar">
            {collapsed ? '›' : '🏨'}
          </div>
          {!collapsed && (
            <>
              <div style={{ flex:1, minWidth:0 }}>
                <p style={{ fontSize:12, fontWeight:700, color:T.white, lineHeight:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {property?.name || 'Reservation'}
                </p>
                <p style={{ fontSize:9, color:T.gold, letterSpacing:1, textTransform:'uppercase', marginTop:2, overflow:'hidden', textOverflow:'ellipsis' }}>
                  {tenantSlug}
                </p>
              </div>
              <button onClick={() => setCollapsed(true)}
                style={{ background:'none', border:'none', color:'#4A5568', cursor:'pointer', fontSize:13, padding:2, flexShrink:0 }}>‹</button>
            </>
          )}
        </div>

        {/* Navigation */}
        <nav style={{ flex:1, padding:'8px 6px', overflowY:'auto', overflowX:'hidden' }}>
          {mainNav.map(item => <NavBtn key={item.id} item={item} />)}
          {!collapsed && (
            <div style={{ marginTop:8 }}>
              <button onClick={() => setSettingsOpen(o => !o)} style={{
                width:'100%', background:'none', border:'none', cursor:'pointer',
                display:'flex', alignItems:'center', justifyContent:'space-between',
                padding:'6px 10px', borderRadius:8, fontFamily:FONTS.body,
              }}>
                <span style={{ fontSize:9, fontWeight:700, color:'#4A6080', letterSpacing:.8, textTransform:'uppercase' }}>Settings</span>
                <span style={{ color:'#4A6080', fontSize:10 }}>{settingsOpen ? '▾' : '›'}</span>
              </button>
              {settingsOpen && settingsNav.map(item => <NavBtn key={item.id} item={item} />)}
            </div>
          )}
          {collapsed && settingsNav.map(item => <NavBtn key={item.id} item={item} />)}
        </nav>

        {/* Module switcher */}
        {otherModules.length > 0 && !collapsed && (
          <div ref={modsRef} style={{ borderTop:`1px solid ${T.navyLight}`, padding:'8px' }}>
            <button onClick={() => setShowMods(s => !s)} style={{
              width:'100%', background:`${T.gold}15`, border:`1px solid ${T.gold}30`,
              borderRadius:9, padding:'8px 12px', cursor:'pointer', fontFamily:FONTS.body,
              display:'flex', alignItems:'center', gap:8,
            }}>
              <span style={{ fontSize:13 }}>🔀</span>
              <span style={{ fontSize:11, fontWeight:700, color:T.gold, flex:1, textAlign:'left' }}>Switch Module</span>
              <span style={{ color:T.gold, fontSize:10 }}>{showMods ? '▴' : '▾'}</span>
            </button>
            {showMods && (
              <div style={{ marginTop:4, background:T.navyMid, borderRadius:9, overflow:'hidden', border:`1px solid ${T.navyLight}` }}>
                {otherModules.map(m => (
                  <button key={m.moduleId} onClick={() => {
                    setShowMods(false);
                    window.parent?.postMessage({ type:'HOTELMIS_SWITCH_MODULE', moduleId:m.moduleId }, '*');
                  }} style={{ width:'100%', background:'none', border:'none', padding:'9px 14px',
                    cursor:'pointer', fontFamily:FONTS.body, display:'flex', alignItems:'center', gap:10 }}
                  onMouseEnter={e => e.currentTarget.style.background = `${T.teal}20`}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                    <span style={{ fontSize:14 }}>{MODULE_ICONS[m.moduleId] || '📦'}</span>
                    <span style={{ fontSize:12, color:T.white, textTransform:'capitalize' }}>{m.name || m.moduleId}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Property footer */}
        {!collapsed && property && (
          <div style={{ padding:'10px 14px', borderTop:`1px solid ${T.navyLight}` }}>
            <p style={{ fontSize:9, color:T.gold, fontWeight:700, letterSpacing:.8, textTransform:'uppercase', marginBottom:2 }}>Active Property</p>
            <p style={{ fontSize:11, color:T.white, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{property.name}</p>
            <p style={{ fontSize:9, color:'#6B7A8D' }}>
              {[property.city, property.country].filter(Boolean).join(', ')}
              {property.currency ? ` · ${property.currency}` : ''}
            </p>
          </div>
        )}
      </aside>

      {/* ── Main ── */}
      <div className="fd-main" style={{ marginLeft:W, flex:1, display:'flex', flexDirection:'column', transition:'margin-left .22s ease', minWidth:0, position:'relative' }}>

        {/* Top bar */}
        <div className="fd-topbar" style={{
          position:'sticky', top:0, zIndex:100, height:54,
          background:T.navy, borderBottom:`1px solid ${T.navyLight}`,
          display:'flex', alignItems:'center', padding:'0 20px',
          boxShadow:'0 2px 16px rgba(0,0,0,.25)', gap:14,
        }}>
          {/* Hamburger — mobile only */}
          <button
            className="mobile-hamburger"
            onClick={() => setMobileOpen(o => !o)}
            aria-label="Toggle navigation"
            style={{
              background:'none', border:`1px solid ${T.navyLight}40`, borderRadius:8,
              width:36, height:36, cursor:'pointer', color:T.white,
              alignItems:'center', justifyContent:'center',
              fontSize:18, flexShrink:0, transition:'background .15s',
            }}>
            {mobileOpen ? '✕' : '☰'}
          </button>

          {/* Breadcrumb */}
          <div style={{ flex:1, minWidth:0, display:'flex', alignItems:'center', gap:8 }}>
            <p style={{ fontSize:14, fontWeight:700, color:T.white, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              {currentLabel}
            </p>
            {property && <>
              <span style={{ color:T.navyLight, flexShrink:0 }}>·</span>
              <p style={{ fontSize:12, color:'#5A7A9A', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{property.name}</p>
            </>}
          </div>

          {/* Clock */}
          <SidebarClock />

          {/* Subscription watch badge — isolated: ticks every second without re-rendering the layout */}
          <IsolatedWatchBadge />

          {/* Online / offline indicator + sync state */}
          <div className="topbar-online" style={{ display:'flex', alignItems:'center', gap:5, flexShrink:0 }}>

            {/* Connection dot + label — transitions silently, never shows "Checking…" */}
            <div style={{ width:7, height:7, borderRadius:'50%',
              background: isOnline ? T.success : T.danger,
              boxShadow: isOnline && !syncState ? `0 0 6px ${T.success}` : 'none',
              transition: 'background .6s ease, box-shadow .6s ease',
              animation: !isOnline ? 'blink 1.2s ease infinite' : 'none' }} />
            <span style={{ fontSize:10, color: isOnline ? '#4A6080' : T.danger,
              fontWeight: isOnline ? 400 : 700,
              transition: 'color .4s ease' }}>
              {isOnline ? (syncState ? 'Syncing' : 'Online') : 'Offline'}
            </span>

            {/* Live sync progress counter */}
            {syncState && (
              <span title={syncState.label}
                style={{ display:'flex', alignItems:'center', gap:4,
                  background:`${T.teal}18`, border:`1px solid ${T.teal}40`,
                  borderRadius:99, padding:'1px 7px', fontSize:9, color:T.teal, fontWeight:700 }}>
                <span style={{ display:'inline-block', animation:'spin .8s linear infinite' }}>⟳</span>
                {syncState.done}/{syncState.total}
              </span>
            )}

            {/* Pending queued badge (offline, not currently syncing) */}
            {!syncState && pendingSync > 0 && (
              <span
                title={`${pendingSync} offline change${pendingSync > 1 ? 's' : ''} — will sync when back online`}
                style={{ background:`${T.gold}28`, border:`1px solid ${T.gold}55`,
                  borderRadius:99, padding:'1px 7px', fontSize:9, color:T.gold,
                  fontWeight:700, cursor:'default' }}>
                {pendingSync} queued
              </span>
            )}

            {/* Conflict badge */}
            {conflictCount > 0 && !syncState && (
              <span
                title={`${conflictCount} conflict${conflictCount > 1 ? 's' : ''} — open Settings to review`}
                onClick={() => setConflictCount(0)}
                style={{ background:`${T.danger}22`, border:`1px solid ${T.danger}55`,
                  borderRadius:99, padding:'1px 7px', fontSize:9, color:T.danger,
                  fontWeight:700, cursor:'pointer' }}>
                ⚠ {conflictCount} conflict{conflictCount > 1 ? 's' : ''}
              </span>
            )}

            {/* Halted badge — network error stopped the queue */}
            {syncHalted && !syncState && isOnline && (
              <span
                title="Sync paused — will retry automatically"
                onClick={() => { setSyncHalted(null); if (!window.pms) _flushGenericQueue(getToken, getTenantSlug); else window.pms.requestSync?.(); }}
                style={{ background:`#7c3aed22`, border:`1px solid #7c3aed55`,
                  borderRadius:99, padding:'1px 7px', fontSize:9, color:'#a78bfa',
                  fontWeight:700, cursor:'pointer' }}>
                ↺ retry sync
              </span>
            )}
          </div>


          {/* Notifications */}
          <div ref={notifRef} style={{ position:'relative', flexShrink:0 }}>
            <button onClick={() => { setShowNotif(s => !s); setNotifRead(true); }} style={{
              background:'none', border:'none', cursor:'pointer', fontSize:18, padding:4, position:'relative',
            }}>
              🔔
              {notifications.length > 0 && !notifRead && (
                <span style={{ position:'absolute', top:0, right:0, minWidth:16, height:16,
                  background:T.danger, borderRadius:8, border:`2px solid ${T.navy}`,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:8, color:'#fff', fontWeight:700, padding:'0 3px' }}>
                  {notifications.length > 9 ? '9+' : notifications.length}
                </span>
              )}
            </button>
            {showNotif && (
              <div style={{ position:'absolute', right:0, top:'calc(100% + 8px)', width:'min(340px, 92vw)',
                background:T.white, borderRadius:12, boxShadow:SHADOW.lg,
                border:`1px solid ${T.border}`, zIndex:300, overflow:'hidden' }}>
                <div style={{ padding:'14px 16px', borderBottom:`1px solid ${T.border}`,
                  display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <p style={{ fontSize:13, fontWeight:700, color:T.navy }}>Notifications</p>
                  <button onClick={loadNotifications} title="Refresh"
                    style={{ background:'none', border:'none', cursor:'pointer', fontSize:13, color:T.teal }}>
                    ↻
                  </button>
                </div>
                {notifications.length === 0 ? (
                  <div style={{ padding:'24px 16px', textAlign:'center' }}>
                    <p style={{ fontSize:13, color:T.muted }}>All clear — no alerts right now</p>
                  </div>
                ) : notifications.map((n, i) => {
                  const meta = NOTIF_META[n.type] || NOTIF_META.default;
                  const urgent = n.priority === 'high';
                  return (
                    <div key={i} style={{ padding:'11px 16px', borderBottom:`1px solid ${T.border}`,
                      display:'flex', gap:10, cursor:'pointer',
                      borderLeft: urgent ? `3px solid ${T.danger}` : `3px solid transparent` }}
                      onMouseEnter={e => e.currentTarget.style.background = T.surface}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <span style={{ fontSize:18, flexShrink:0 }}>{meta.icon}</span>
                      <div style={{ flex:1, minWidth:0 }}>
                        <p style={{ fontSize:12, color: urgent ? T.danger : T.navy,
                          fontWeight: urgent ? 700 : 500, lineHeight:1.4 }}>{n.message}</p>
                        <p style={{ fontSize:10, color:T.muted, marginTop:2 }}>{meta.label}</p>
                      </div>
                      {urgent && <span style={{ fontSize:9, fontWeight:700, color:T.danger,
                        background:`${T.danger}15`, borderRadius:99, padding:'2px 6px', flexShrink:0, alignSelf:'center' }}>
                        URGENT
                      </span>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Profile */}
          <div ref={profileRef} style={{ position:'relative', flexShrink:0 }}>
            <button onClick={() => setShowProfile(s => !s)} style={{
              background:`${T.teal}22`, border:`1px solid ${T.teal}40`, borderRadius:24,
              padding:'5px 10px 5px 6px', cursor:'pointer', display:'flex', alignItems:'center', gap:8,
              transition:'all .15s',
            }}>
              <div style={{ width:30, height:30, borderRadius:'50%', background:`linear-gradient(135deg, ${T.gold}, ${T.teal})`,
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:11, fontWeight:800, color:T.navy, flexShrink:0 }}>{initials}</div>
              <div className="topbar-clock topbar-username" style={{ textAlign:'left', lineHeight:1.2 }}>
                <p style={{ fontSize:12, color:T.white, fontWeight:700, margin:0, whiteSpace:'nowrap' }}>{displayName}</p>
                {userRole && (
                  <p style={{ fontSize:9, color:`${T.teal}CC`, margin:0, textTransform:'uppercase', letterSpacing:.5, fontWeight:600 }}>
                    {userRole.replace(/_/g,' ')}
                  </p>
                )}
              </div>
              <span style={{ fontSize:9, color:`${T.teal}CC` }}>▾</span>
            </button>
            {showProfile && (
              <div style={{ position:'absolute', right:0, top:'calc(100% + 8px)', width:220,
                background:T.white, borderRadius:14, boxShadow:SHADOW.lg,
                border:`1px solid ${T.border}`, zIndex:300, overflow:'hidden' }}>
                {/* Profile header in dropdown */}
                <div style={{ padding:'14px 16px', background:`linear-gradient(135deg, ${T.navy}08, ${T.teal}06)`, borderBottom:`1px solid ${T.border}` }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{ width:36, height:36, borderRadius:'50%', background:`linear-gradient(135deg, ${T.gold}, ${T.teal})`,
                      display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:800, color:T.navy, flexShrink:0 }}>
                      {initials}
                    </div>
                    <div style={{ minWidth:0 }}>
                      <p style={{ fontSize:13, fontWeight:700, color:T.navy, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{displayName}</p>
                      {userRole && <p style={{ fontSize:10, color:T.muted, textTransform:'uppercase', letterSpacing:.5 }}>{userRole.replace(/_/g,' ')}</p>}
                    </div>
                  </div>
                </div>
                {[
                  { icon:'👤', label:'My Profile',     action:'profile' },
                  { icon:'⚙️',  label:'Property Setup', action:'settings',     roles:['ADMIN','TENANT_ADMIN','MANAGER'] },
                  { icon:'✉️',  label:'Email Config',   action:'email-config', roles:['ADMIN','TENANT_ADMIN'], desktopHide:true },
                ].filter(item => canAccess(item, userRole) && !(isDesktop && item.desktopHide)).map(item => (
                  <button key={item.action} onClick={() => { setShowProfile(false); handleNavigate(item.action); }}
                    style={{ width:'100%', background:'none', border:'none', padding:'11px 16px',
                      cursor:'pointer', fontFamily:FONTS.body, display:'flex', alignItems:'center', gap:10, textAlign:'left' }}
                    onMouseEnter={e => e.currentTarget.style.background = T.surface}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                    <span style={{ fontSize:15 }}>{item.icon}</span>
                    <span style={{ fontSize:13, color:T.navy }}>{item.label}</span>
                  </button>
                ))}
                <div style={{ borderTop:`1px solid ${T.border}` }}>
                  <button onClick={() => { setShowProfile(false); onLogout(); }}
                    style={{ width:'100%', background:'none', border:'none', padding:'11px 16px',
                      cursor:'pointer', fontFamily:FONTS.body, display:'flex', alignItems:'center', gap:10, textAlign:'left' }}
                    onMouseEnter={e => e.currentTarget.style.background = T.dangerPale}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                    <span style={{ fontSize:15 }}>🚪</span>
                    <span style={{ fontSize:13, color:T.danger, fontWeight:600 }}>Log Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Subscription status banner — slim strip immediately under the topbar.
            Handles three states: unreachable (amber), expiry warning (amber/red),
            and expired (full-screen overlay via position:fixed). */}
        {/* Subscription status banner — isolated so its re-renders stay local */}
        <IsolatedSubscriptionWatchdog onLogout={onLogout} tenantSlug={tenantSlug} />

        {/* Slim sync progress stripe — full-width gradient bar under the topbar */}
        {syncState && (
          <div style={{ flexShrink:0, height:3, background:`${T.teal}22`, overflow:'hidden' }}>
            <div style={{
              height: '100%',
              background: `linear-gradient(90deg, ${T.teal}, ${T.gold})`,
              width:  syncState.total > 0
                ? `${Math.max(
                    window.pms ? 15 : 6,   // desktop: min 15% — "in progress" feedback
                    Math.round((syncState.done / syncState.total) * 100)
                  )}%`
                : '30%',
              transition: 'width .4s ease',
              boxShadow: `0 0 8px ${T.teal}88`,
            }} />
          </div>
        )}

        {/* Sync overlay — appears only while flushing queued items (non-blocking: user can still read the screen) */}
        {syncLocked && pendingSync > 0 && (
          <div style={{
            position: 'absolute', top: 57, right: 18, zIndex: 9998,
            pointerEvents: 'none',
          }}>
            <div style={{
              background: T.white, borderRadius: 14,
              padding: '14px 20px', boxShadow: SHADOW.lg,
              border: `1px solid ${T.border}`,
              display: 'flex', alignItems: 'flex-start', gap: 12, minWidth: 240,
            }}>
              <span style={{ fontSize: 20, animation: 'spin .9s linear infinite', display: 'inline-block' }}>⟳</span>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: T.navy, margin: '0 0 3px' }}>
                  Syncing offline changes…
                </p>
                <p style={{ fontSize: 11, color: '#64748b', margin: '0 0 8px' }}>
                  {syncState
                    ? `${syncState.done} of ${syncState.total} — ${syncState.label ?? ''}`
                    : `${pendingSync} item${pendingSync > 1 ? 's' : ''} in queue`}
                </p>
                {/* Mini progress bar inside the card */}
                <div style={{ height: 4, borderRadius: 2, background: `${T.teal}22`, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%',
                    background: `linear-gradient(90deg, ${T.teal}, ${T.gold})`,
                    width: syncState?.total > 0
                      ? `${Math.max(6, Math.round((syncState.done / syncState.total) * 100))}%`
                      : '25%',
                    transition: 'width .4s ease',
                    borderRadius: 2,
                  }} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="fd-content" style={{ flex:1, padding:'22px', overflowY:'auto', minWidth:0 }}>
          {offlineBoot && (
            <div style={{
              background: `${T.gold}18`, border: `1px solid ${T.gold}40`, borderRadius: 10,
              padding: '10px 16px', marginBottom: 16, fontSize: 12, color: T.slate, lineHeight: 1.5,
            }}>
              📵 Showing cached data — server unreachable. Changes will sync when connection returns.
            </div>
          )}
          {children}
        </div>

        {/* Floating status chip — bottom-right, non-intrusive */}

        {/* Offline chip */}
        {!isOnline && (
          <div style={{
            position:'fixed', bottom:18, right:18, zIndex:9990,
            background:'#1e293b', color:'#e2e8f0',
            borderRadius:24, padding:'7px 14px',
            display:'flex', alignItems:'center', gap:8,
            boxShadow:'0 4px 20px rgba(0,0,0,.4)',
            fontSize:12, fontWeight:500,
            animation:'fadeUp .3s ease',
          }}>
            <div style={{ width:7, height:7, borderRadius:'50%', background:'#f87171',
              flexShrink:0, animation:'blink 1.4s ease infinite' }} />
            <span>Offline — working safely</span>
            {pendingSync > 0 && (
              <span style={{ background:'rgba(255,200,60,.18)', border:'1px solid rgba(255,200,60,.35)',
                borderRadius:99, padding:'1px 8px', fontSize:11, color:'#fbbf24', fontWeight:700 }}>
                {pendingSync} queued
              </span>
            )}
          </div>
        )}

        {/* Synced confirmation chip — flashes briefly after sync completes */}
        {isOnline && !syncState && !syncLocked && pendingSync === 0 && conflictCount === 0 && !syncHalted && offlineBoot && (
          <div key="synced-chip" style={{
            position:'fixed', bottom:18, right:18, zIndex:9990,
            background:'#052e16', color:'#86efac',
            borderRadius:24, padding:'7px 14px',
            display:'flex', alignItems:'center', gap:8,
            boxShadow:'0 4px 20px rgba(0,0,0,.4)',
            fontSize:12, fontWeight:600,
            animation:'fadeUp .3s ease',
          }}>
            <div style={{ width:7, height:7, borderRadius:'50%', background:'#4ade80', flexShrink:0 }} />
            <span>All changes synced</span>
          </div>
        )}

        {/* Halted chip — sync stopped due to network error */}
        {syncHalted && isOnline && !syncState && (
          <div
            onClick={() => { setSyncHalted(null); if (!window.pms) _flushGenericQueue(getToken, getTenantSlug); else window.pms.requestSync?.(); }}
            style={{
              position:'fixed', bottom:18, right:18, zIndex:9990,
              background:'#2e1065', color:'#c4b5fd',
              borderRadius:24, padding:'7px 14px',
              display:'flex', alignItems:'center', gap:8,
              boxShadow:'0 4px 20px rgba(0,0,0,.4)',
              fontSize:12, fontWeight:600, cursor:'pointer',
              animation:'fadeUp .3s ease',
            }}>
            <span>⚠</span>
            <span>Sync paused — tap to retry</span>
            {syncHalted.remaining > 0 && (
              <span style={{ background:'rgba(255,255,255,.12)', borderRadius:99, padding:'1px 7px', fontSize:11 }}>
                {syncHalted.remaining} left
              </span>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin    {from{transform:rotate(0)}to{transform:rotate(360deg)}}
        @keyframes fadeUp  {from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
        @keyframes scaleIn {from{opacity:0;transform:scale(.95)}to{opacity:1;transform:scale(1)}}
        @keyframes slideUp {from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes blink   {0%,100%{opacity:1}50%{opacity:.3}}
        .fade-up{animation:fadeUp .28s ease}
        .scale-in{animation:scaleIn .2s ease}
        *{box-sizing:border-box}
        ::-webkit-scrollbar{width:6px;height:6px}
        ::-webkit-scrollbar-track{background:transparent}
        ::-webkit-scrollbar-thumb{background:#CBD5E0;border-radius:3px}
        ::-webkit-scrollbar-thumb:hover{background:#A0AEC0}
        @media print{aside,.no-print{display:none!important}body{background:white}}
      `}</style>
    </div>
  );
}